import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';

import { FileService } from '../../../common/services/file.service';
import { FileInfo } from '../../../common/models/file';
import { MatSnackBar, MatSnackBarConfig, MatSnackBarModule } from '@angular/material/snack-bar';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatListModule } from '@angular/material/list';
import { MatCardModule } from '@angular/material/card';
import { GenericDialogComponent } from '../dialog/dialog';
import { MatDialog } from '@angular/material/dialog';


@Component({
    selector: 'app-file-upload',
    templateUrl: './file.component.html',
    styleUrls: ['./file.component.scss'],
    imports: [
    MatButtonModule,
    MatIconModule,
    MatListModule,
    MatSnackBarModule,
    MatCardModule,
    MatTooltipModule
]
})
export class FileUploadComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  @Input() storageLocation!: string; // Must be provided by the parent component

  existingFiles: FileInfo[] = [];

  private readonly snackbarConfig: MatSnackBarConfig = {
    duration: 3000,
    horizontalPosition: 'right',
    verticalPosition: 'top'
  };

  constructor(
    private fileService: FileService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    if (!this.storageLocation) {
      throw new Error('storageLocation is required');
    }

    this.loadExistingFiles();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Load existing files from the server
  private loadExistingFiles(): void {
    this.fileService.listDocuments(this.storageLocation).pipe(takeUntil(this.destroy$)).subscribe({
      next: (files: FileInfo[]) => {
        this.existingFiles = files;
      },
      error: (error) => {
        console.error(error);
        this.snackBar.open('Pogreška prilikom učitavanja datoteka.', 'Zatvori', this.snackbarConfig);
      }
    });
  }

  // Handle file selection and automatically upload
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files) return;

    const files: File[] = Array.from(input.files);

    files.forEach(file => {
      this.uploadFile(file);
    });

    // Reset the input value to allow selecting the same file again if needed
    input.value = '';
  }

  private uploadFile(file: File): void {
    this.fileService.uploadDocument(file, this.storageLocation).pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        this.existingFiles.push({ name: file.name, link: response.link });
        this.snackBar.open(`${file.name} uspješno prenesena.`, 'Zatvori', this.snackbarConfig);
      },
      error: (error) => {
        console.error(error);
        this.snackBar.open(`Greška prilikom prijenosa datoteke ${file.name}`, 'Zatvori', this.snackbarConfig);
      }
    });
  }

  deleteFile(file: FileInfo): void {
    const dialogRef = this.dialog.open(GenericDialogComponent, {
      width: '50%',
      data: {
        title: 'Potvrdite brisanje',
        message: `Jeste li sigurni da želite obrisati datoteku ${file.name}?`,
        confirmButtonText: 'Da',
        cancelButtonText: 'Ne',
      },
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
      if (result === true) {
        this.fileService.deleteDocument(file.link).pipe(takeUntil(this.destroy$)).subscribe({
          next: () => {
            this.existingFiles = this.existingFiles.filter(f => f.link !== file.link);
            this.snackBar.open(`Datoteka uspješno obrisana ${file.name}`, 'Zatvori', this.snackbarConfig);
          },
          error: (error) => {
            console.error(error);
            this.snackBar.open(`Greška prilikom brisanja datoteke ${file.name}`, 'Zatvori', this.snackbarConfig);
          }
        });
      }
    });
  }

  downloadFile(file: FileInfo): void {
     this.fileService.downloadDocument(file.link).pipe(takeUntil(this.destroy$)).subscribe({
       next: (blob) => {
         const url = window.URL.createObjectURL(blob);
         const a = document.createElement('a');
         a.href = url;
         a.download = file.name;
         a.click();
         window.URL.revokeObjectURL(url);
       },
       error: (error) => {
         console.error(error);
         this.snackBar.open(`Greška prilikom preuzimanja datoteke ${file.name}`, 'Zatvori', this.snackbarConfig);
       }
     });
   }

  trackByFile(index: number, file: File): string {
    return file.name;
  }

  trackByFileInfo(index: number, file: FileInfo): string {
    return file.link;
  }
}